import type * as React from 'react';
import { Group, Text, Button, ActionIcon, Tooltip, Box } from '@mantine/core';
import { IconPin, IconX, IconMaximize, IconMinimize } from '@tabler/icons-react';
import { KianSessionUsageText } from '../KianSessionUsage';

export interface KianFlyoutHeaderProps {
  title: string;
  /** Offenes Gespräch — für die Zeile „Gespräch bisher: … Tokens · … €" */
  sessionId?: string | null;
  isFullscreen: boolean;
  followPageContext: boolean;
  /** Nur im Floating-Modus: Drag zum Verschieben */
  dragEnabled?: boolean;
  dragHandleListeners?: {
    onPointerDown?: (e: React.PointerEvent<Element>) => void;
    onPointerMove?: (e: React.PointerEvent<Element>) => void;
    onPointerUp?: (e: React.PointerEvent<Element>) => void;
    onPointerCancel?: (e: React.PointerEvent<Element>) => void;
  };
  onFullscreen: () => void;
  onPinToggle: () => void;
  onAllChats: () => void;
  onClose: () => void;
}

export function KianFlyoutHeader({
  title,
  sessionId,
  isFullscreen,
  followPageContext,
  dragEnabled,
  dragHandleListeners,
  onFullscreen,
  onPinToggle,
  onAllChats,
  onClose,
}: KianFlyoutHeaderProps) {
  return (
    <Group
      justify="space-between"
      p="xs"
      wrap="nowrap"
      gap="xs"
      style={{
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        background: 'transparent',
        cursor: dragEnabled ? 'grab' : undefined,
        touchAction: dragEnabled ? 'none' : undefined,
      }}
      {...dragHandleListeners}
    >
      <Tooltip label={title} withArrow disabled={title.length < 40} position="bottom-start">
        <Box style={{ minWidth: 0, flex: 1, pointerEvents: 'none' }}>
          <Text fw={600} size="sm" lineClamp={1}>
            {title}
          </Text>
          {/* Der Kopf ist Zieh-Griff (pointer-events aus) — die Zeile selbst nimmt Hover für den Tooltip an */}
          <KianSessionUsageText sessionId={sessionId} prefix="Gespräch: " display="block" truncate style={{ pointerEvents: 'auto' }} />
        </Box>
      </Tooltip>
      <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }} onPointerDown={(e) => e.stopPropagation()}>
        <Tooltip label={isFullscreen ? 'Vollbild verlassen' : 'Vollbild'} withArrow>
          <ActionIcon
            variant={isFullscreen ? 'light' : 'subtle'}
            color={isFullscreen ? 'blue' : 'gray'}
            onClick={() => onFullscreen()}
            aria-label="Vollbild"
            aria-pressed={isFullscreen}
          >
            {isFullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip
          label={
            followPageContext
              ? 'Kontext einfrieren (bleibt beim Wechseln der Seite)'
              : 'Kontext an aktuelle Seite koppeln'
          }
          withArrow
        >
          <ActionIcon
            variant={followPageContext ? 'subtle' : 'filled'}
            color={followPageContext ? 'gray' : 'cyberOrange'}
            onClick={() => onPinToggle()}
            aria-label="Kontext anheften"
            aria-pressed={!followPageContext}
            data-testid="kian-pin-button"
          >
            <IconPin size={18} />
          </ActionIcon>
        </Tooltip>
        <Button variant="default" size="xs" onClick={() => onAllChats()}>
          Alle Chats
        </Button>
        <Tooltip label="Schließen" withArrow>
          <ActionIcon variant="subtle" color="gray" onClick={() => onClose()} aria-label="Schließen">
            <IconX size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
}
